import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  Eye,
  Target,
  BarChart3,
  DollarSign,
  Hash,
  ExternalLink,
  X,
  Play,
  Image,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { Card } from '../ui';
import { socialMediaService } from '../../services/socialMediaService';
import useSocialMediaStore from '../../stores/socialMediaStore';
import { toastService } from '../../services/toastService';
import BrandVideoPlayer from './BrandVideoPlayer';
import { useDrawerNavigation } from '../../contexts/DrawerNavigationContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface BrandData {
  brandName: string;
  postCount: number;
  totalDurationSeconds: number;
  totalDetections: number;
  totalEmv: string;
  avgConfidence: number;
}

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
  frameTimeMs: number;
}

interface Appearance {
  startTimeSeconds: number;
  endTimeSeconds: number;
  durationSeconds: number;
  confidence: number;
  frameCount: number;
  boundingBoxes: BoundingBox[];
}

interface PostDetail {
  postId: number;
  platformPostId: string;
  postUrl: string;
  postTitle: string;
  platform: {
    name: string;
    displayName: string;
  };
  postType: string;
  postEmv: string;
  postCreateDate: string;
  brandName: string;
  totalDurationSeconds: number;
  detectionCount: number;
  confidence: number;
  partitionKey: number;
  appearances: Appearance[];
  media: {
    type: string;
    url: string;
    width: number;
    height: number;
    durationSeconds: number;
  };
}

interface TimelineDataPoint {
  date: string;
  detectionCount: number;
  totalDuration: number;
  avgConfidence: number;
  postCount: number;
}

interface BrandTimelineResponse {
  brandName: string;
  timeline: TimelineDataPoint[];
  summary: {
    totalDays: number;
    daysWithData: number;
    totalDetections: number;
    totalDuration: number;
    totalPosts: number;
  };
  filters: any;
}

interface PlatformBreakdownData {
  platform: {
    id: number;
    name: string;
    displayName: string;
  };
  uniqueDetections: number;
  percentage: number;
  postCount: number;
  totalDetections: number;
  totalDurationSeconds: number;
  avgConfidence: number;
}

interface BrandPlatformBreakdownResponse {
  brandName: string;
  platforms: PlatformBreakdownData[];
  summary: {
    totalPlatforms: number;
    totalUniqueDetections: number;
    totalPosts: number;
    totalDetections: number;
    totalDurationSeconds: number;
  };
}


interface BrandDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  brand: BrandData | null;
}

const BrandDetailsDrawer: React.FC<BrandDetailsDrawerProps> = ({
  isOpen,
  onClose,
  brand
}) => {
  const { currentCustomerId, currentPeriod, selectedPlatformIds, customDateRange } = useSocialMediaStore();
  const { canGoBack, goBack, getBreadcrumbs } = useDrawerNavigation();
  
  const [postDetails, setPostDetails] = useState<PostDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'posts'>('overview');
  const [mediaError, setMediaError] = useState<{ [key: number]: boolean }>({});
  const [expandedPosts, setExpandedPosts] = useState<{ [key: number]: boolean }>({});
  const [expandedAppearances, setExpandedAppearances] = useState<{ [key: number]: boolean }>({});
  const [mediaData, setMediaData] = useState<{ [key: number]: string }>({});
  const [mediaLoading, setMediaLoading] = useState<{ [key: number]: boolean }>({});
  const [timelineData, setTimelineData] = useState<BrandTimelineResponse | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [platformData, setPlatformData] = useState<BrandPlatformBreakdownResponse | null>(null);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformError, setPlatformError] = useState<string | null>(null);

  // Helper function to format duration
  const formatDuration = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(1);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Helper function to format confidence percentage
  const formatConfidence = (confidence: number) => {
    return `${Math.round(confidence * 100)}%`;
  };

  // Helper function to format time
  const formatTime = (seconds: number) => {
    return `${seconds.toFixed(1)}s`;
  };

  // Helper function to get appearance summary
  const getAppearanceSummary = (appearances: Appearance[]) => {
    if (!appearances || appearances.length === 0) return null;
    
    const earliestStart = Math.min(...appearances.map(a => a.startTimeSeconds));
    const latestEnd = Math.max(...appearances.map(a => a.endTimeSeconds));
    const totalDuration = latestEnd - earliestStart;
    const avgConfidence = appearances.reduce((sum, a) => sum + a.confidence, 0) / appearances.length;
    
    return {
      count: appearances.length,
      startTime: earliestStart,
      endTime: latestEnd,
      duration: totalDuration,
      avgConfidence
    };
  };

  // Toggle appearances expansion
  const toggleAppearancesExpansion = (postId: number) => {
    setExpandedAppearances(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get platform icon
  const getPlatformIcon = (platformName: string) => {
    switch (platformName.toLowerCase()) {
      case 'instagram':
        return <Instagram className="h-5 w-5 text-pink-500" />;
      case 'twitter':
        return <Twitter className="h-5 w-5 text-blue-400" />;
      case 'youtube':
        return <Youtube className="h-5 w-5 text-red-500" />;
      case 'linkedin':
        return <Linkedin className="h-5 w-5 text-blue-600" />;
      default:
        return <BarChart3 className="h-5 w-5 text-gray-400" />;
    }
  };

  // Helper function to get platform colors for charts
  const getPlatformColor = (platformName: string) => {
    switch (platformName.toLowerCase()) {
      case 'instagram':
        return '#ec4899'; // pink-500
      case 'tiktok':
        return '#000000'; // black
      case 'twitter':
        return '#60a5fa'; // blue-400
      case 'youtube':
        return '#ef4444'; // red-500
      case 'linkedin':
        return '#2563eb'; // blue-600
      case 'facebook':
        return '#3b82f6'; // blue-500
      default:
        return '#6b7280'; // gray-500
    }
  };

  // Handle media error
  const handleMediaError = (postId: number) => {
    setMediaError(prev => ({ ...prev, [postId]: true }));
  };

  // Toggle post expansion
  const togglePostExpansion = (postId: number, partitionKey: number) => {
    const isExpanding = !expandedPosts[postId];
    setExpandedPosts(prev => ({ ...prev, [postId]: isExpanding }));
    
    // Load media when expanding if not already loaded
    if (isExpanding && !mediaData[postId] && !mediaError[postId]) {
      loadMediaData(postId, partitionKey);
    }
  };

  // Load media data from backend
  const loadMediaData = async (postId: number, partitionKey: number) => {
    setMediaLoading(prev => ({ ...prev, [postId]: true }));

    try {
      // preferType 'video': this feeds BrandVideoPlayer, so a thumbnail is not an acceptable
      // fallback — it would render as a broken player. No video means show the error state.
      const response = await socialMediaService.getPostMedia(postId, partitionKey, false, 'video');

      if (response.success && response.data) {
        // Handle both array and object response structures
        let mediaUrl: string | undefined;

        if (Array.isArray(response.data.media) && response.data.media.length > 0) {
          // Array structure (PostInsightsDrawer format)
          mediaUrl = response.data.media[0].media || response.data.media[0].mediaUrl;
        } else if (response.data.media && typeof response.data.media === 'string') {
          // Direct media string (BrandDetailsDrawer format)
          mediaUrl = response.data.media;
        }

        if (mediaUrl) {
          setMediaData(prev => ({ ...prev, [postId]: mediaUrl }));
        } else {
          setMediaError(prev => ({ ...prev, [postId]: true }));
        }
      } else {
        setMediaError(prev => ({ ...prev, [postId]: true }));
      }
    } catch (error) {
      setMediaError(prev => ({ ...prev, [postId]: true }));
    } finally {
      setMediaLoading(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Load brand timeline data
  const loadBrandTimeline = async () => {
    if (!brand || !currentCustomerId) return;

    setTimelineLoading(true);
    setTimelineError(null);

    try {
      const response = await socialMediaService.getBrandTimeline({
        customerId: currentCustomerId,
        brandName: brand.brandName,
        period: currentPeriod,
        platformIds: selectedPlatformIds || [],
        customStartDate: customDateRange?.start,
        customEndDate: customDateRange?.end
      });

      if (response.success && response.data) {
        setTimelineData(response.data);
        setTimelineError(null);
      } else {
        setTimelineError('Failed to load brand timeline');
        console.error('Failed to load timeline data:', response.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error loading timeline data:', error);
      setTimelineError('Failed to load brand timeline');
    } finally {
      setTimelineLoading(false);
    }
  };

  // Load brand platform breakdown data
  const loadBrandPlatformBreakdown = async () => {
    if (!brand || !currentCustomerId) return;

    setPlatformLoading(true);
    setPlatformError(null);

    try {
      const response = await socialMediaService.getBrandPlatformBreakdown({
        customerId: currentCustomerId,
        brandName: brand.brandName,
        period: currentPeriod,
        customStartDate: customDateRange?.start,
        customEndDate: customDateRange?.end
      });

      if (response.success && response.data) {
        setPlatformData(response.data);
        setPlatformError(null);
      } else {
        setPlatformError('Failed to load platform breakdown');
        console.error('Failed to load platform breakdown data:', (response as any).message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error loading platform breakdown data:', error);
      setPlatformError('Failed to load platform breakdown');
    } finally {
      setPlatformLoading(false);
    }
  };

  // Load brand post details
  const loadBrandDetails = async () => {
    if (!brand || !currentCustomerId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await socialMediaService.getBrandDetails(
        currentCustomerId,
        brand.brandName,
        currentPeriod,
        5,
        currentPeriod === 'custom' && customDateRange ? customDateRange.start : undefined,
        currentPeriod === 'custom' && customDateRange ? customDateRange.end : undefined
      );

      if (response.success && response.data) {
        setPostDetails(response.data.brandDetails as unknown as PostDetail[]);
      } else {
        setError('Failed to load brand details');
        toastService.error('Error', 'Failed to load brand details');
      }
    } catch (error) {
      console.error('Error loading brand details:', error);
      setError('Failed to load brand details');
      toastService.error('Error', 'Failed to load brand details');
    } finally {
      setLoading(false);
    }
  };

  // Load data when drawer opens and brand is selected
  useEffect(() => {
    if (isOpen && brand) {
      loadBrandDetails();
      loadBrandTimeline();
      loadBrandPlatformBreakdown();
      
      // Auto-navigate to Posts tab if coming from post insights
      if (canGoBack()) {
        setActiveTab('posts');
      }
    }
  }, [isOpen, brand, currentCustomerId, currentPeriod, selectedPlatformIds, customDateRange, canGoBack]);

  if (!brand) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-75 z-[9999]"
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-[10000] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg">
                  <Eye className="h-5 w-5 text-white" />
                </div>
                <div>
                  {canGoBack() && (
                    <div className="mb-1 flex items-center text-xs">
                      <button
                        onClick={goBack}
                        className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-2"
                      >
                        <ArrowLeft className="h-3 w-3 mr-1" />
                        Back
                      </button>
                      <div className="flex items-center text-gray-500">
                        {getBreadcrumbs().map((crumb, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && <ChevronRight className="h-3 w-3 mx-1" />}
                            {crumb.onClick ? (
                              <button
                                onClick={crumb.onClick}
                                className="hover:text-gray-700 hover:underline"
                              >
                                {crumb.label}
                              </button>
                            ) : (
                              <span className="text-gray-700 font-medium">{crumb.label}</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                  <h2 className="text-lg font-semibold text-gray-900">{brand.brandName}</h2>
                  <p className="text-sm text-gray-500">Brand Detection Insights</p>
                </div>
              </div>
              <button
                onClick={canGoBack() ? goBack : onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center border-b border-gray-200 px-4">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'posts'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Posts ({postDetails.length})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {/* Tab Content */}
                {activeTab === 'overview' ? (
                  <div className="space-y-4">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 gap-3">
                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium text-gray-500">Total EMV</div>
                            <div className="text-base font-bold text-gray-900">{brand.totalEmv}</div>
                          </div>
                          <div className="p-1.5 bg-green-100 rounded-md">
                            <DollarSign className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium text-gray-500">Total Posts</div>
                            <div className="text-base font-bold text-gray-900">{brand.postCount}</div>
                          </div>
                          <div className="p-1.5 bg-blue-100 rounded-md">
                            <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium text-gray-500">Total Detections</div>
                            <div className="text-base font-bold text-gray-900">{brand.totalDetections}</div>
                          </div>
                          <div className="p-1.5 bg-purple-100 rounded-md">
                            <Hash className="h-3.5 w-3.5 text-purple-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium text-gray-500">Avg Confidence</div>
                            <div className="text-base font-bold text-gray-900">{formatConfidence(brand.avgConfidence)}</div>
                          </div>
                          <div className="p-1.5 bg-orange-100 rounded-md">
                            <Target className="h-3.5 w-3.5 text-orange-600" />
                          </div>
                        </div>
                      </Card>
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-2 gap-4">
                    {/* Detection Timeline Chart */}
                    <Card className="p-4">
                      <div className="mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Detection Timeline</h3>
                        {timelineData?.summary && (
                          <div className="text-xs text-gray-600">
                            {timelineData.summary.daysWithData} of {timelineData.summary.totalDays} days with detections
                          </div>
                        )}
                      </div>

                      {timelineLoading ? (
                        <div className="flex items-center justify-center h-56">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        </div>
                      ) : timelineError ? (
                        <div className="flex items-center justify-center h-56">
                          <div className="text-center">
                            <AlertCircle className="h-8 w-8 mx-auto mb-3 text-red-500" />
                            <p className="text-red-600 font-medium text-sm">{timelineError}</p>
                            <button 
                              onClick={loadBrandTimeline}
                              className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Try Again
                            </button>
                          </div>
                        </div>
                      ) : timelineData && timelineData.timeline.length > 0 ? (
                        <div className="h-56">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={timelineData.timeline}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11 }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                              />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip
                                labelFormatter={(value) => `Date: ${value}`}
                                formatter={(value, name) => {
                                  if (name === 'detectionCount') return [value, 'Detections'];
                                  if (name === 'avgConfidence') return [`${(value as number * 100).toFixed(1)}%`, 'Avg Confidence'];
                                  if (name === 'totalDuration') return [`${(value as number).toFixed(1)}s`, 'Total Duration'];
                                  if (name === 'postCount') return [value, 'Posts'];
                                  return [value, name];
                                }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="detectionCount" 
                                stroke="#6366f1" 
                                strokeWidth={2}
                                dot={false}
                                name="detectionCount"
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-56 text-gray-500">
                          <div className="text-center">
                            <BarChart3 className="h-8 w-8 mx-auto mb-3 text-gray-400" />
                            <p className="text-sm">No detection data available for the selected period</p>
                          </div>
                        </div>
                      )}
                    </Card>

                    {/* Platform Distribution Chart */}
                    <Card className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Platform Distribution</h3>
                      
                      {platformLoading ? (
                        <div className="flex items-center justify-center h-56">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        </div>
                      ) : platformError ? (
                        <div className="flex items-center justify-center h-56">
                          <div className="text-center">
                            <AlertCircle className="h-8 w-8 mx-auto mb-3 text-red-500" />
                            <p className="text-red-600 font-medium text-sm">{platformError}</p>
                            <button 
                              onClick={loadBrandPlatformBreakdown}
                              className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Try Again
                            </button>
                          </div>
                        </div>
                      ) : platformData && platformData.platforms.length > 0 ? (
                        <div>
                          {/* Pie Chart */}
                          <div className="h-48 mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={platformData.platforms.map(p => ({
                                    name: p.platform.displayName,
                                    value: p.uniqueDetections,
                                    percentage: p.percentage,
                                    color: getPlatformColor(p.platform.name)
                                  }))}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={70}
                                  dataKey="value"
                                  label={({ percentage }) => `${percentage.toFixed(1)}%`}
                                >
                                  {platformData.platforms.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={getPlatformColor(entry.platform.name)} />
                                  ))}
                                </Pie>
                                <Tooltip 
                                  formatter={(value, name) => [`${value} detections`, name]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          
                          {/* Legend and Details */}
                          <div className="space-y-2">
                            {platformData.platforms.map((platform) => (
                              <div key={platform.platform.id} className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <div 
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: getPlatformColor(platform.platform.name) }}
                                  />
                                  <div>
                                    <div className="font-medium text-gray-900 text-sm">{platform.platform.displayName}</div>
                                    <div className="text-xs text-gray-500">
                                      {platform.postCount} posts • {formatConfidence(platform.avgConfidence)}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-gray-900 text-sm">{platform.uniqueDetections}</div>
                                  <div className="text-xs text-gray-500">{platform.percentage.toFixed(1)}%</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-56 text-gray-500">
                          <div className="text-center">
                            <BarChart3 className="h-8 w-8 mx-auto mb-3 text-gray-400" />
                            <p className="text-sm">No platform data available for the selected period</p>
                          </div>
                        </div>
                      )}
                    </Card>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Posts Tab - Expandable List */}
                    {loading && (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                          <p className="text-sm text-gray-600">Loading post details...</p>
                        </div>
                      </div>
                    )}

                    {!loading && !error && postDetails.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-xs text-gray-500">Click on a post to expand it and view the media containing this brand.</p>
                        {postDetails.map((post, index) => (
                          <Card key={`${post.postId}-${index}`} className="overflow-hidden">
                            {/* Post Header - Always Visible */}
                            <div
                              className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                              onClick={() => togglePostExpansion(post.postId, post.partitionKey)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  {getPlatformIcon(post.platform.name)}
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{post.platform.displayName}</div>
                                    <div className="text-xs text-gray-500">{formatDate(post.postCreateDate)}</div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                                    {post.postType}
                                  </span>
                                  <div className="text-right">
                                    <div className="font-medium text-gray-900">{post.postEmv}</div>
                                    <div className="text-sm text-gray-500">EMV</div>
                                  </div>
                                  <button className="p-1 hover:bg-gray-200 rounded">
                                    {expandedPosts[post.postId] ? (
                                      <ChevronUp className="h-4 w-4 text-gray-400" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4 text-gray-400" />
                                    )}
                                  </button>
                                </div>
                              </div>
                              <div className="mt-2">
                                <p className="text-sm text-gray-600 line-clamp-2">{post.postTitle}</p>
                              </div>
                            </div>

                            {/* Expanded Content */}
                            {expandedPosts[post.postId] && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="border-t border-gray-200"
                              >
                                <div className="p-4 space-y-4">
                                  {/* Post Metrics */}
                                  <div className="grid grid-cols-4 gap-4 text-xs">
                                    <div className="text-center">
                                      <div className="font-medium text-gray-900">{formatDuration(post.totalDurationSeconds)}</div>
                                      <div className="text-gray-500">Duration</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="font-medium text-gray-900">{post.detectionCount}</div>
                                      <div className="text-gray-500">Detections</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="font-medium text-gray-900">{formatConfidence(post.confidence)}</div>
                                      <div className="text-gray-500">Confidence</div>
                                    </div>
                                    <div className="text-center">
                                      <a
                                        href={post.postUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                        View Post
                                      </a>
                                    </div>
                                  </div>

                                  {/* Brand Appearances */}
                                  {post.appearances && post.appearances.length > 0 && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium text-gray-900 flex items-center">
                                          <Target className="h-4 w-4 mr-2 text-purple-500" />
                                          Brand Appearances
                                        </h4>
                                        <button
                                          onClick={() => toggleAppearancesExpansion(post.postId)}
                                          className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
                                        >
                                          {expandedAppearances[post.postId] ? (
                                            <>Hide Details <ChevronUp className="h-4 w-4 ml-1" /></>
                                          ) : (
                                            <>View Details <ChevronDown className="h-4 w-4 ml-1" /></>
                                          )}
                                        </button>
                                      </div>

                                      {(() => {
                                        const summary = getAppearanceSummary(post.appearances);
                                        if (!summary) return null;

                                        return (
                                          <div className="space-y-3">
                                            {/* Summary */}
                                            <div className="bg-purple-50 rounded-lg p-3">
                                              <div className="flex items-center justify-between text-sm">
                                                <span className="text-purple-700 font-medium">
                                                  {summary.count} detections across {formatTime(summary.duration)}
                                                </span>
                                                <span className="text-purple-600">
                                                  {formatTime(summary.startTime)} - {formatTime(summary.endTime)}
                                                </span>
                                              </div>
                                              <div className="mt-2">
                                                <div className="flex items-center">
                                                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                                    <div
                                                      className="bg-purple-500 h-2 rounded-full"
                                                      style={{ width: `${summary.avgConfidence * 100}%` }}
                                                    />
                                                  </div>
                                                  <span className="text-sm text-purple-600">
                                                    {formatConfidence(summary.avgConfidence)} avg confidence
                                                  </span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Detailed Appearances */}
                                            {expandedAppearances[post.postId] && (
                                              <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="space-y-2"
                                              >
                                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                                  Detection Timeline
                                                </div>
                                                {post.appearances.map((appearance, index) => (
                                                  <div key={index} className="bg-gray-50 rounded p-2 text-sm">
                                                    <div className="flex items-center justify-between">
                                                      <span className="font-medium text-gray-700">
                                                        Detection #{index + 1}
                                                      </span>
                                                      <span className="text-gray-500">
                                                        {appearance.frameCount} frames
                                                      </span>
                                                    </div>
                                                    <div className="mt-1 flex items-center justify-between">
                                                      <span className="text-gray-600">
                                                        {formatTime(appearance.startTimeSeconds)} - {formatTime(appearance.endTimeSeconds)}
                                                        <span className="text-gray-400 ml-1">
                                                          ({formatTime(appearance.durationSeconds)})
                                                        </span>
                                                      </span>
                                                      <span className="font-medium text-gray-700">
                                                        {formatConfidence(appearance.confidence)}
                                                      </span>
                                                    </div>
                                                  </div>
                                                ))}
                                              </motion.div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* Media Content */}
                                  <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                                    {mediaLoading[post.postId] ? (
                                      <div className="flex flex-col items-center justify-center h-64 bg-gray-100">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
                                        <p className="text-gray-500">Loading media...</p>
                                      </div>
                                    ) : mediaError[post.postId] ? (
                                      <div className="flex flex-col items-center justify-center h-64 bg-gray-100">
                                        {post.media.type === 'video' ? (
                                          <Play className="h-12 w-12 text-gray-400 mb-2" />
                                        ) : (
                                          <Image className="h-12 w-12 text-gray-400 mb-2" />
                                        )}
                                        <p className="text-gray-500">
                                          {post.media.type === 'video' ? 'Video' : 'Image'} not available
                                        </p>
                                      </div>
                                    ) : mediaData[post.postId] ? (
                                      <>
                                        {post.media.type === 'video' && post.appearances && post.appearances.length > 0 ? (
                                          <BrandVideoPlayer
                                            videoSrc={mediaData[post.postId]}
                                            appearances={post.appearances}
                                            brandName={post.brandName}
                                            onError={() => handleMediaError(post.postId)}
                                          />
                                        ) : post.media.type === 'video' ? (
                                          <video
                                            controls
                                            className="w-full max-h-96 object-contain"
                                            onError={() => handleMediaError(post.postId)}
                                          >
                                            <source src={mediaData[post.postId]} />
                                            Your browser does not support the video tag.
                                          </video>
                                        ) : (
                                          <img
                                            src={mediaData[post.postId]}
                                            alt={post.postTitle}
                                            className="w-full max-h-96 object-contain"
                                            onError={() => handleMediaError(post.postId)}
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-64 bg-gray-100">
                                        {post.media.type === 'video' ? (
                                          <Play className="h-12 w-12 text-gray-400 mb-2" />
                                        ) : (
                                          <Image className="h-12 w-12 text-gray-400 mb-2" />
                                        )}
                                        <p className="text-gray-500">Click to load media</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Error State */}
                    {error && !loading && (
                      <Card className="p-6 text-center">
                        <div className="text-red-500 mb-2">
                          <BarChart3 className="h-8 w-8 mx-auto" />
                        </div>
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Error Loading Data</h3>
                        <p className="text-xs text-gray-600 mb-4">{error}</p>
                        <button
                          onClick={loadBrandDetails}
                          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                        >
                          Try Again
                        </button>
                      </Card>
                    )}

                    {/* Empty State */}
                    {!loading && !error && postDetails.length === 0 && (
                      <Card className="p-8 text-center">
                        <Eye className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                        <h3 className="text-sm font-medium text-gray-900 mb-1">No Post Details Available</h3>
                        <p className="text-xs text-gray-600">
                          No post details found for {brand.brandName} in the selected time period.
                        </p>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BrandDetailsDrawer;